import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { AddCircleOutline, CancelOutlined } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { DonationProvider } from '@joindeed/calculate-fees';

import { currencies } from 'utils/currencyFormat';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import {
  Currency,
  ProviderType,
  useCreateGivingAccountMutation,
  GivingAccountsSummaryQuery,
  GivingAccountsSummaryDocument,
} from 'generated/graphql';
import { showErrorAction } from 'containers/Notification/actions';

const NameInput = controlledTextField<FieldValues>()('name', {
  rules: { required: true },
});

const CurrencyCodeInput = controlledTextField<FieldValues>()('currencyCode', {
  rules: { required: true },
});

type FieldValues = {
  name: string;
  currencyCode: string;
  provider: string;
};

export const CreateGivingAccountModal = ({ title }: { title: string }): JSX.Element => {
  const history = useHistory();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const dispatch = useDispatch();
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      currencyCode: Currency.Usd,
      provider: DonationProvider.PayPal,
    },
  });

  const [createGivingAccountMutation] = useCreateGivingAccountMutation({
    update(cache, { data }) {
      if (!data?.createGivingAccount) {
        return;
      }

      const existingData = cache.readQuery<GivingAccountsSummaryQuery>({
        query: GivingAccountsSummaryDocument,
      });

      if (existingData?.givingAccounts) {
        cache.writeQuery({
          query: GivingAccountsSummaryDocument,
          data: {
            givingAccounts: [...existingData.givingAccounts, data.createGivingAccount],
          },
        });
      }
    },
    onError: () => {
      dispatch(showErrorAction('Failed to create a Giving Account, please try again'));
    },
  });

  const handleClose = () => {
    setCreateModalOpen(false);
    reset();
  };

  const handleSubmit = reactHookFormHandleSubmit(async (data) => {
    await createGivingAccountMutation({
      variables: {
        name: data.name,
        currencyCode: data.currencyCode as Currency,
        provider: data.provider as ProviderType,
      },
    });
    handleClose();
    history.push('/donations/ledger');
  });

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<AddCircleOutline />}
        onClick={() => setCreateModalOpen(true)}
      >
        {title}
      </Button>
      <Modal
        open={createModalOpen}
        disableAutoFocus
        style={{
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalContents
          onClickClose={handleClose}
          body={
            <>
              <ModalTitle>Create a new Giving Account</ModalTitle>
              <Box paddingTop={4}>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <NameInput
                      controllerProps={{ control }}
                      label="Account Name"
                      helperText={errors.name?.message}
                    />
                    <CurrencyCodeInput
                      controllerProps={{ control }}
                      label="Currency"
                      select
                      helperText={errors.currencyCode?.message}
                      disabled
                    >
                      {Object.values(currencies).map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </CurrencyCodeInput>
                  </Stack>
                </form>
              </Box>
            </>
          }
          actions={
            <>
              <ModalButton
                variant="outlined"
                disabled={isSubmitting}
                onClick={handleClose}
                endIcon={<CancelOutlined />}
              >
                Cancel
              </ModalButton>
              &nbsp;
              <ModalButton
                disabled={isSubmitting}
                onClick={async () => {
                  await handleSubmit();
                }}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AddCircleOutline />
                  )
                }
              >
                {isSubmitting ? 'Creating…' : 'Create Account'}
              </ModalButton>
            </>
          }
        />
      </Modal>
    </>
  );
};
